import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Shield } from 'lucide-react'
import { apiClient } from '../services/api'
import toast from 'react-hot-toast'
import { LoadingSpinner } from '../components/ui/LoadingSpinner'
import { SEO } from '../components/SEO'
import { SEOService } from '../services/seoService'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    
    try {
      await apiClient.forgotPassword({ email })
      setIsSuccess(true)
      toast.success('If an account exists, a reset link has been sent.')
    } catch (_error) {
      toast.error('An error occurred. Please try again.')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <SEO 
        title="Recover Password | Level 2 Logic"
        description="Reset your CMMC compliance platform password securely."
        keywords={['password reset', 'cmmc login']}
        structuredData={SEOService.generateStructuredData('landing')}
      />

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="flex justify-center flex-col items-center">
          <Shield className="h-12 w-12 text-blue-600 mb-2" />
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            CMMC Platform
          </h2>
          <h3 className="mt-2 text-center text-lg font-medium text-gray-600">
            Password Recovery
          </h3>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {isSuccess ? (
            <div className="text-center">
              <div className="mb-4 text-green-600 font-medium">Reset link sent!</div>
              <p className="text-sm text-gray-600 mb-6">
                Please check your inbox at <strong>{email}</strong> for instructions on how to reset your password.
              </p>
              <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500">
                Return to Login
              </Link>
            </div>
          ) : (
            <form className="space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                >
                  {isSubmitting ? <LoadingSpinner size="sm" className="text-white" /> : 'Send Reset Link'}
                </button>
              </div>
            </form>
          )}
          
          <div className="mt-6 flex items-center justify-center">
             <div className="text-sm">
                <Link to="/login" className="font-medium text-gray-500 hover:text-gray-900">
                  Wait, I remember it. Back to login.
                </Link>
             </div>
          </div>
        </div>
      </div>
    </div>
  )
}
